import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

export interface FormButton {
  type: 'submit' | 'reset' | 'button';
  text: string;
  icon?: string;
  class?: string;
  disabled?: boolean;
  show?: boolean;
  validate?: boolean;
  onClick?: (formData: any) => void;
}

export interface DynamicFormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'date' | 'select' | 'radio' | 'checkbox' | 'section' | 'lims-dropdown';
  required?: boolean;
  width?: number; // 1-12 grid columns
  row?: number; // Position in form
  options?: { value: any; label: string }[];
  validators?: any[];
  maxLength?: number;
  value?: any;
  disabled?: boolean;
  hidden?: boolean;
  toggleField?: string; // Field name that controls visibility
  toggleValue?: any; // Value that triggers visibility
  // Additional properties for lims-dropdown
  keyId?: string; // ID field key for lims-dropdown
  keyName?: string; // Display field key for lims-dropdown
  placeholder?: string; // Placeholder text
  noEntriesFoundLabel?: string; // Text shown when no entries found
  multiple?: boolean; // Enable multiple selection
  allItemLabel?: string; // Label for select all option
  class?: string; // CSS class for the dropdown
  // Validation messages
  errorMessages?: {
    required?: string;
    email?: string;
    pattern?: string;
    maxlength?: string;
    min?: string;
    max?: string;
  };
}

@Component({
  selector: 'app-dynamic-form-lims',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss']
})
export class DynamicFormComponent implements OnInit {
  @Input() fields: DynamicFormField[] = [];
  @Input() formTitle = '';
  @Input() showButtonIcons = true;
  @Input() buttons: FormButton[] = [
    {
      type: 'reset',
      text: 'Làm mới',
      icon: 'fa fa-refresh',
      class: 'refresh-Family-btn',
      show: true
    },
    {
      type: 'submit',
      text: 'Lưu',
      icon: 'fa fa-save',
      class: 'add-Family-btn',
      disabled: true
    }
  ];
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formReset = new EventEmitter<void>();
  @Output() fieldValueChange = new EventEmitter<{fieldName: string, value: any}>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.createForm();
  }

  private createForm() {
    const group = {};
    
    this.fields.forEach(field => {
      const validators = [];
      
      if (field.required) {
        validators.push(Validators.required);
      }
      
      if (field.type === 'email') {
        validators.push(Validators.email);
      }

      if (field.maxLength) {
        validators.push(Validators.maxLength(field.maxLength));
      }
      
      if (field.validators) {
        validators.push(...field.validators);
      }

      if (field.type === 'checkbox' && field.options) {
        group[field.name] = [field.value || []];
      } else {
        group[field.name] = [{
          value: field.value || '',
          disabled: field.disabled
        }, validators];
      }
    });
    
    this.form = this.fb.group(group);
  }

  onCheckboxChange(event: any, fieldName: string, optionValue: any) {
    const control = this.form.get(fieldName);
    if (!control) return;

    const currentValues = control.value as any[] || [];
    
    if (event.target.checked) {
      control.setValue([...currentValues, optionValue]);
    } else {
      control.setValue(currentValues.filter(v => v !== optionValue));
    }

    // Emit event for checkbox change
    this.fieldValueChange.emit({
      fieldName: `${fieldName}_${optionValue}`,
      value: event.target.checked
    });
  }
onSubmit() {
  if (this.form.valid) {
    const formValue = { ...this.form.value };
    
    // Clean up checkbox individual controls and handle lims-dropdown null values
    this.fields.forEach(field => {
      if (field.type === 'checkbox' && field.options) {
        field.options.forEach(option => {
          delete formValue[`${field.name}_${option.value}`];
        });
      }
      
      // Handle lims-dropdown values
      if (field.type === 'lims-dropdown') {
        if (field.multiple) {
          // Return empty array if no selection for multiple dropdown
          formValue[field.name] = formValue[field.name] || [];
        } else if (formValue[field.name] === 'null') {
          // Convert 'null' string to null for single dropdown
          formValue[field.name] = null;
        }
      }
    });

    this.formSubmit.emit(formValue);
  } else {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }
}

onReset() {
  // Reset form controls
  this.form.reset();

  // Reset each control to its initial value
  this.fields.forEach(field => {
    if (field.type !== 'section') {
      const control = this.form.get(field.name);
      if (control) {
        control.setValue(field.value || null);
        control.markAsUntouched();
      }
    }
  });

  // Emit reset event
  this.formReset.emit();
}

handleButtonClick(btn: FormButton) {
  // Get form data
  const formValue = { ...this.form.value };
  
  // Clean up checkbox controls and handle lims-dropdown values
  this.fields.forEach(field => {
    if (field.type === 'checkbox' && field.options) {
      field.options.forEach(option => {
        delete formValue[`${field.name}_${option.value}`];
      });
    }
    
    if (field.type === 'lims-dropdown') {
      if (field.multiple) {
        formValue[field.name] = formValue[field.name] || [];
      } else if (formValue[field.name] === 'null') {
        formValue[field.name] = null;
      }
    }
  });

  // Check if validation is required
  if (btn.validate !== false) {
    // Validate form
    if (!this.form.valid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
      return;
    }
  }

  // Call button's onClick with form data
  btn.onClick?.(formValue);
}

  getErrorMessage(fieldName: string): string {
    const control = this.form.get(fieldName);
    const field = this.fields.find(f => f.name === fieldName);
    
    if (!control || !field) return '';

    // Get the first validation error
    const error = control.errors ? Object.keys(control.errors)[0] : null;
    if (!error) return '';

    // Return custom error message if provided
    if (field.errorMessages?.[error]) {
      const message = field.errorMessages[error];
      // Replace placeholders in message
      return message
        .replace('{min}', control.errors?.[error]?.min)
        .replace('{max}', control.errors?.[error]?.max)
        .replace('{maxlength}', field.maxLength?.toString())
        .replace('{pattern}', field.validators?.find(v => v instanceof RegExp)?.toString() || '');
    }

    // Default error messages
    switch (error) {
      case 'required':
        return 'Vui lòng nhập thông tin';
      case 'email':
        return 'Email không hợp lệ';
      case 'maxlength':
        return `Không được nhập quá ${field.maxLength} ký tự`;
      case 'min':
        return `Giá trị phải lớn hơn hoặc bằng ${control.errors[error].min}`;
      case 'max':
        return `Giá trị phải nhỏ hơn hoặc bằng ${control.errors[error].max}`;
      case 'pattern':
        return 'Giá trị không hợp lệ';
      default:
        return '';
    }
  }
}